import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionSheet, YStack, createMqStyles, tokens } from '@fhs/ui';

import { useLoadGiftCardMutation } from '../../mutations/use-load-gift-card';
import { useGiftCardTransferInquiryQuery } from '../../queries/use-get-balance';
import { GiftCardsActionSheetHeader } from '../gift-cards-action-sheet-header';

import { StepAddCardForm } from './step-add-card-form';
import { StepCardSummary } from './step-card-summary';
import { StepCheckBalance } from './step-check-balance';
import { GiftCardActionSheetProps, Step } from './types';

export const GiftCardActionSheet = ({
  headerTitle,
  isVisible,
  isCheckBalanceFlow = false,
  currentStep = Step.ADD_CARD_FORM,
  currentDigitalBalance = 0,
  handleBalanceUpdate = (_newBalance: number) => {},
  setCurrentStep,
  onClose = () => {},
  onGiftCardSaved,
  cardId,
  onCardIdChange,
  pin,
  onPinChange,
}: GiftCardActionSheetProps) => {
  const [isLoadingGiftCard, setIsLoadingGiftCard] = useState(false);
  const [giftCardBalance, setGiftCardBalance] = useState(0);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');

  const mqStyles = useMqStyles();

  const { mutateAsync: saveGiftCardAsync, isPending: isSavingGiftCard } = useLoadGiftCardMutation();
  const { data, isPending: balanceIsPending, isError, error } = useGiftCardTransferInquiryQuery({
    cardNumber: cardId,
    pin,
    fetchBalance: isLoadingGiftCard,
  });

  const handleBackNavigation = useCallback(() => {
    setCurrentStep(prev => {
      switch (prev) {
        case Step.CHECK_BALANCE:
          return Step.ADD_CARD_FORM;

        case Step.CARD_SUMMARY:
          return isCheckBalanceFlow ? Step.CHECK_BALANCE : Step.ADD_CARD_FORM;

        case Step.ADD_CARD_FORM:
        default:
          return prev; // nothing to go back to
      }
    });
  }, [setCurrentStep, isCheckBalanceFlow]);

  const handleChangePress = useCallback(() => {
    setCurrentStep(Step.ADD_CARD_FORM);
  }, [setCurrentStep]);

  const handleAddCardContinue = useCallback(() => {
    setBalanceErrorMessage('');
    setIsLoadingGiftCard(true);
  }, []);

  const handleCardIdChange = useCallback(
    (newCardId: string) => {
      setBalanceErrorMessage('');
      onCardIdChange(newCardId);
    },
    [onCardIdChange]
  );

  const handlePinChange = useCallback(
    (newPin: string) => {
      setBalanceErrorMessage('');
      onPinChange(newPin);
    },
    [onPinChange]
  );

  useEffect(() => {
    if (isError && isLoadingGiftCard) {
      const duplicateErrorMessage =
        'This gift card is already in use. Please check the card details and try again.';
      const defaultErrorMessage = isCheckBalanceFlow
        ? 'There was an error checking your gift card balance. Please verify the card details and try again.'
        : 'There was an error adding your gift card. Please verify the card details and try again.';

      const resolvedErrorMessage = (() => {
        if (error && typeof error === 'object') {
          const graphQLErrors = 'graphQLErrors' in error ? (error as { graphQLErrors?: unknown }).graphQLErrors : undefined;
          if (Array.isArray(graphQLErrors)) {
            const graphQLErrorMessage = graphQLErrors.find(
              (graphQLError): graphQLError is { message: string } =>
                Boolean(graphQLError) && typeof (graphQLError as { message?: unknown }).message === 'string'
            )?.message;

            if (graphQLErrorMessage?.includes('already in use')) {
              return duplicateErrorMessage;
            }

            if (graphQLErrorMessage) {
              return graphQLErrorMessage;
            }
          }

          const errorMessage = 'message' in error ? (error as { message?: unknown }).message : undefined;
          if (typeof errorMessage === 'string') {
            if (errorMessage.includes('already in use')) {
              return duplicateErrorMessage;
            }
            return errorMessage;
          }
        }

        return defaultErrorMessage;
      })();

      setGiftCardBalance(0);
      setIsLoadingGiftCard(false);
      setBalanceErrorMessage(resolvedErrorMessage || defaultErrorMessage);
      return;
    }

    // move to next step when we have the balance loaded (only if we triggered the query)
    if (isLoadingGiftCard && !balanceIsPending && data?.currentBalance) {
      setGiftCardBalance(data.currentBalance);
      setIsLoadingGiftCard(false);
      setBalanceErrorMessage('');

      if (isCheckBalanceFlow) {
        setCurrentStep(Step.CHECK_BALANCE);
        return;
      }

      setCurrentStep(Step.CARD_SUMMARY);
    }
  }, [
    balanceIsPending,
    data?.currentBalance,
    isCheckBalanceFlow,
    isError,
    isLoadingGiftCard,
    error,
    setCurrentStep,
  ]);

  const handleSaveCard = useCallback(async () => {
    try {
      await saveGiftCardAsync({ cardNumber: cardId, pin });
      handleBalanceUpdate(giftCardBalance);

      if (onGiftCardSaved) {
        onGiftCardSaved();
      } else {
        onClose();
      }
    } catch {
      onClose();
    }
  }, [saveGiftCardAsync, cardId, pin, handleBalanceUpdate, onGiftCardSaved, onClose]);

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case Step.ADD_CARD_FORM:
        return (
          <StepAddCardForm
            cardId={cardId}
            onCardIdChange={handleCardIdChange}
            pin={pin}
            onPinChange={handlePinChange}
            isLoadingGiftCard={isLoadingGiftCard}
            onContinue={handleAddCardContinue}
            errorMessage={balanceErrorMessage}
          />
        );
      case Step.CARD_SUMMARY:
        return (
          <StepCardSummary
            cardId={cardId}
            onChangePress={handleChangePress}
            onSave={handleSaveCard}
            isLoading={isSavingGiftCard}
            balance={giftCardBalance}
            digitalBalance={currentDigitalBalance}
          />
        );
      case Step.CHECK_BALANCE:
        return (
          <StepCheckBalance
            cardId={cardId}
            onChangePress={handleChangePress}
            onAddToYourAccountPress={() => setCurrentStep(Step.CARD_SUMMARY)}
            balance={giftCardBalance}
          />
        );
    }
  }, [
    currentStep,
    cardId,
    pin,
    isLoadingGiftCard,
    handleAddCardContinue,
    handleCardIdChange,
    handlePinChange,
    handleChangePress,
    handleSaveCard,
    isSavingGiftCard,
    giftCardBalance,
    currentDigitalBalance,
    balanceErrorMessage,
    setCurrentStep,
  ]);

  return (
    <ActionSheet
      onRequestClose={onClose}
      onClose={onClose}
      isVisible={isVisible}
      grabber={false}
      noPanGestureDetection
      containerStyle={{ minHeight: '30%' }}
      drawerProps={{
        style: { flex: 1, backgroundColor: tokens.colors.$white, borderRadius: 12 },
      }} // Needed to make the flat list scroll inside the actionsheet
    >
      <GiftCardsActionSheetHeader
        headerTitle={headerTitle}
        onClose={onClose}
        onPressPrev={handleBackNavigation}
        disableBackButton={currentStep === Step.ADD_CARD_FORM}
      />
      <YStack style={mqStyles.contentWrapper}>{stepContent}</YStack>
    </ActionSheet>
  );
};

const useMqStyles = createMqStyles({
  contentWrapper: {
    $base: {
      padding: 16,
    },
    $gteDesktopLg: {
      paddingHorizontal: 28,
    },
  },
});
